// Home.js - 로그인 시 상품 랜덤 추가
import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { API_BASE_URL } from '../services/api';
import ProductCard from '../components/ProductCard';
import WishlistButton from '../components/WishlistButton';
import './Home.css';


function DropdownPortal({ children, isOpen, buttonRef }) {
	const [position, setPosition] = useState(null);
	const [isReady, setIsReady] = useState(false);

	useEffect(() => {
		if (isOpen && buttonRef.current) {
			const rect = buttonRef.current.getBoundingClientRect();
			setPosition({
				top: rect.bottom + window.scrollY + 8,
				left: rect.left + window.scrollX
			});
			
			requestAnimationFrame(() => {
				setIsReady(true);
			});
		} else {
			setIsReady(false);
		}
	}, [isOpen, buttonRef]);

	if (!isOpen || !position || !isReady) return null;

	return ReactDOM.createPortal(
		<div 
			className="tag-options-portal" 
			style={{
				position: 'absolute',
				top: `${position.top}px`,
				left: `${position.left}px`,
				zIndex: 10000,
				opacity: 1,
				animation: 'fadeIn 0.15s ease-out'
			}}
		>
			{children}
		</div>,
		document.body
	);
}

function Home() {
	const [products, setProducts] = useState([]);
	const [totalCount, setTotalCount] = useState(0);
	const [totalPages, setTotalPages] = useState(0);
	const [categories, setCategories] = useState([]);
	const [selectedLevel1, setSelectedLevel1] = useState(null);
	const [selectedLevel2, setSelectedLevel2] = useState(null);
	const [selectedLevel3, setSelectedLevel3] = useState(null);
	const [currentPage, setCurrentPage] = useState(1);
	const [loading, setLoading] = useState(true);
	const [notices, setNotices] = useState([]);
	
	const [situationMode, setSituationMode] = useState(false);
	const [situationTags, setSituationTags] = useState({
		weather: [],
		where: [],
		mood: [],
		situation: [],
		season_event: [],
		style: [],
		color_mood: [],
		age_target: []
	});
	const [allTags, setAllTags] = useState({});
	const [showDropdown, setShowDropdown] = useState(null);
	const [isNavigatingBack, setIsNavigatingBack] = useState(false);
	const buttonRefs = useRef({});
	const dropdownRef = useRef(null);
	
	const navigate = useNavigate();
	const location = useLocation();
	const [searchParams, setSearchParams] = useSearchParams();
	
	const searchTerm = searchParams.get('search') || '';


	// ✅ 뒤로가기 시 스크롤 위치 즉시 복원 (로딩 전)
	useEffect(() => {
		const savedPosition = sessionStorage.getItem('homeScrollPosition');
		if (savedPosition) {
			const scrollY = parseInt(savedPosition);
			window.scrollTo(0, scrollY);
			document.body.scrollTop = scrollY;
			document.documentElement.scrollTop = scrollY;
		}
	}, []);

	// ✅ 로딩 완료 후 다시 한번 복원
	useEffect(() => {
		if (!loading) {
			const savedPosition = sessionStorage.getItem('homeScrollPosition');
			if (savedPosition) {
				const scrollY = parseInt(savedPosition);
				window.scrollTo(0, scrollY);
				document.body.scrollTop = scrollY;
				document.documentElement.scrollTop = scrollY;
				sessionStorage.removeItem('homeScrollPosition');
			}
		}
	}, [loading]);

	// ✅ 상품 클릭 시 스크롤 위치 저장 함수
	const handleProductClick = () => {
		sessionStorage.setItem('homeScrollPosition', window.scrollY.toString());
	};

	useEffect(() => {
		function handleClickOutside(event) {
			if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
				const clickedButton = Object.values(buttonRefs.current).some(
					ref => ref && ref.contains(event.target)
				);
				if (!clickedButton) {
					setShowDropdown(null);
				}
			}
		}

		document.addEventListener('mousedown', handleClickOutside);
		return () => document.removeEventListener('mousedown', handleClickOutside);
	}, []);

	useEffect(() => {
		const init = async () => {
			try {
				await fetchCategories();
				await fetchSituationTags();
				await fetchNotices();
			} catch (error) {
				console.error('초기화 실패:', error);
			}
		};
		
		init();
	}, []);

	useEffect(() => {
		if (categories.length === 0 || Object.keys(allTags).length === 0) return;
		
		if (isNavigatingBack) {
			setIsNavigatingBack(false);
			return;
		}
		
		const mode = searchParams.get('mode');
		if (mode === 'situation') {
			setSituationMode(true);
			
			const newTags = {
				weather: searchParams.get('weather')?.split(',').map(Number).filter(Boolean) || [],
				where: searchParams.get('where')?.split(',').map(Number).filter(Boolean) || [],
				mood: searchParams.get('mood')?.split(',').map(Number).filter(Boolean) || [],
				situation: searchParams.get('situation')?.split(',').map(Number).filter(Boolean) || [],
				season_event: searchParams.get('season_event')?.split(',').map(Number).filter(Boolean) || [],
				style: searchParams.get('style')?.split(',').map(Number).filter(Boolean) || [],
				color_mood: searchParams.get('color_mood')?.split(',').map(Number).filter(Boolean) || [],
				age_target: searchParams.get('age_target')?.split(',').map(Number).filter(Boolean) || []
			};
			
			setSituationTags(newTags);
			return;
		} else {
			setSituationMode(false);
			
			const cat1Id = searchParams.get('cat1');
			const cat2Id = searchParams.get('cat2');
			const cat3Id = searchParams.get('cat3');
			
			if (cat1Id) {
				const category = categories.find(c => c.id === parseInt(cat1Id));
				if (category) {
					setSelectedLevel1(category);
					
					if (cat2Id && category.children) {
						const subCategory = category.children.find(c => c.id === parseInt(cat2Id));
						if (subCategory) {
							setSelectedLevel2(subCategory);
							
							if (cat3Id && subCategory.children) {
								const subSubCategory = subCategory.children.find(c => c.id === parseInt(cat3Id));
								if (subSubCategory) {
									setSelectedLevel3(subSubCategory);
								} else {
									setSelectedLevel3(null);
								}
							} else {
								setSelectedLevel3(null);
							}
						} else {
							setSelectedLevel2(null);
							setSelectedLevel3(null);
						}
					} else {
						setSelectedLevel2(null);
						setSelectedLevel3(null);
					}
				}
			} else {
				setSelectedLevel1(null);
				setSelectedLevel2(null);
				setSelectedLevel3(null);
			}
			
			const categoryParam = searchParams.get('category');
			if (categoryParam && !cat1Id) {
				if (categoryParam === '전체') {
					setSelectedLevel1(null);
					setSelectedLevel2(null);
					setSelectedLevel3(null);
				} else {
					const category = categories.find(cat => cat.name === categoryParam);
					if (category) {
						setSelectedLevel1(category);
						setSelectedLevel2(null);
						setSelectedLevel3(null);
						
						const params = new URLSearchParams(searchParams);
						params.delete('category');
						params.set('cat1', category.id);
						setSearchParams(params, { replace: true });
					}
				}
			}
		}
		
		const pageParam = searchParams.get('page');
		if (pageParam) {
			setCurrentPage(parseInt(pageParam));
		} else {
			setCurrentPage(1);
		}
		
	}, [searchParams, categories, allTags]);

	useEffect(() => {
		const mode = searchParams.get('mode');
		
		if (mode === 'situation') {
			const hasAnyTags = Object.values(situationTags).some(tags => tags && tags.length > 0);
			if (hasAnyTags) {
				searchBySituation(currentPage);
			}
		} else {
			fetchData(currentPage);
		}
	}, [currentPage]);

	useEffect(() => {
		if (categories.length === 0 || Object.keys(allTags).length === 0) {
			return;
		}
		
		if (!location.state) {
			return;
		}
		
		if (location.state.situationTag) {
			const tagName = location.state.situationTag;
			
			setSituationMode(true);
			setSelectedLevel1(null);
			setSelectedLevel2(null);
			setSelectedLevel3(null);
			
			let foundCode = null;
			let foundCategory = null;
			
			Object.entries(allTags).forEach(([category, tags]) => {
				Object.entries(tags).forEach(([code, name]) => {
					if (name === tagName) {
						foundCode = parseInt(code);
						foundCategory = category;
					}
				});
			});
			
			if (foundCode && foundCategory) {
				const newTags = {
					weather: [],
					where: [],
					mood: [],
					situation: [],
					season_event: [],
					style: [],
					color_mood: [],
					age_target: [],
					[foundCategory]: [foundCode]
				};
				setSituationTags(newTags);
				
				const params = new URLSearchParams();
				params.set('mode', 'situation');
				params.set(foundCategory, foundCode.toString());
				setSearchParams(params);
			}
			
			setCurrentPage(1);
			window.history.replaceState({}, document.title);
			return;
		}
		
		if (location.state.situationMode && location.state.fromButton) {
			setSituationMode(true);
			setSelectedLevel1(null);
			setSelectedLevel2(null);
			setSelectedLevel3(null);
			
			setSituationTags({
				weather: [],
				where: [],
				mood: [],
				situation: [],
				season_event: [],
				style: [],
				color_mood: [],
				age_target: []
			});
			
			const params = new URLSearchParams();
			params.set('mode', 'situation');
			setSearchParams(params);
			
			setCurrentPage(1);
			window.history.replaceState({}, document.title);
			return;
		}
		
		if (location.state.reset) {
			setSituationMode(false);
			setSelectedLevel1(null);
			setSelectedLevel2(null);
			setSelectedLevel3(null);
			setCurrentPage(1);
			setSearchParams({});
			fetchData(1);
			window.history.replaceState({}, document.title);
			return;
		}
		
		if (location.state.selectedCategory) {
			const categoryName = location.state.selectedCategory;
			
			setSituationMode(false);
			
			if (categoryName === '전체') {
				setSelectedLevel1(null);
				setSelectedLevel2(null);
				setSelectedLevel3(null);
				setCurrentPage(1);
				setSearchParams({});
				fetchData(1);
			} else {
				const category = categories.find(cat => cat.name === categoryName);
				if (category) {
					const params = new URLSearchParams();
					params.set('cat1', category.id);
					
					setSelectedLevel1(category);
					
					if (location.state.autoSelectLevel2 && category.children) {
						const level2 = category.children.find(
							cat => cat.name === location.state.autoSelectLevel2
						);
						if (level2) {
							params.set('cat2', level2.id);
							setSelectedLevel2(level2);
							
							if (location.state.autoSelectLevel3 && level2.children) {
								const level3 = level2.children.find(
									cat => cat.name === location.state.autoSelectLevel3
								);
								if (level3) {
									params.set('cat3', level3.id);
									setSelectedLevel3(level3);
								}
							}
						}
					} else {
						setSelectedLevel2(null);
						setSelectedLevel3(null);
					}
					
					setSearchParams(params);
					setCurrentPage(1);
				}
			}
			window.history.replaceState({}, document.title);
		}
		
	}, [location.state, categories, allTags]);

	useEffect(() => {
		if (categories.length === 0) return;
		
		const mode = searchParams.get('mode');
		
		if (mode === 'situation') {
			const hasAnyTags = Object.values(situationTags).some(tags => tags && tags.length > 0);
			if (hasAnyTags) {
				searchBySituation(currentPage);
			}
		} else {
			fetchData(currentPage);
		}
	}, [searchParams, categories]);

	useEffect(() => {
		const handleLogoClick = () => {
			setSituationMode(false);
			setSelectedLevel1(null);
			setSelectedLevel2(null);
			setSelectedLevel3(null);
			setCurrentPage(1);
			
			setSituationTags({
				weather: [],
				where: [],
				mood: [],
				situation: [],
				season_event: [],
				style: [],
				color_mood: [],
				age_target: []   // ⬅️ 여기 콤마 있어야 할 수도?
			});
			
			setSearchParams({});
			fetchData(1);
		};

		window.addEventListener('logoClick', handleLogoClick);
		
		return () => {
			window.removeEventListener('logoClick', handleLogoClick);
		};
	}, []);

	useEffect(() => {
		const handleHeaderCategoryClick = (e) => {
			const categoryName = e.detail;
			
			setSituationMode(false);
			
			if (categoryName === '전체') {
				setSelectedLevel1(null);
				setSelectedLevel2(null);
				setSelectedLevel3(null);
				setCurrentPage(1);
				setSearchParams({});
				fetchData(1);
			} else {
				const category = categories.find(cat => cat.name === categoryName);
				if (category) {
					setSelectedLevel1(category);
					setSelectedLevel2(null);
					setSelectedLevel3(null);
					setCurrentPage(1);
					
					const params = new URLSearchParams();
					params.set('cat1', category.id);
					setSearchParams(params);
				}
			}
		};

		window.addEventListener('headerCategoryClick', handleHeaderCategoryClick);
		return () => window.removeEventListener('headerCategoryClick', handleHeaderCategoryClick);
	}, [categories]);

	useEffect(() => {
		const hasAnyTags = Object.values(situationTags).some(tags => tags.length > 0);
		
		if (situationMode && hasAnyTags) {
			searchBySituation(currentPage);
		}
	}, [situationTags, situationMode]);

	const fetchData = async (page = 1) => {
		try {
			setLoading(true);
			
			const params = new URLSearchParams(searchParams);
			params.set('page', page);
			
			const response = await fetch(
				`${API_BASE_URL}/products/products/?${params.toString()}`
			);
			const data = await response.json();
			
			setProducts(data.results || []);
			setTotalCount(data.count || 0);
			setTotalPages(data.total_pages || 0);
			
		} catch (error) {
			console.error('상품 로딩 실패:', error);
			setProducts([]);
			setTotalCount(0);
			setTotalPages(0);
		} finally {
			setLoading(false);
		}
	};

	const fetchCategories = async () => {
		try {
			const response = await fetch(`${API_BASE_URL}/products/categories/`);
			
			if (!response.ok) {
				throw new Error(`HTTP ${response.status}`);
			}
			
			const data = await response.json();
			
			let categoryList = [];
			
			if (Array.isArray(data)) {
				categoryList = data;
			} else if (data.results && Array.isArray(data.results)) {
				categoryList = data.results;
			} else {
				console.error('예상치 못한 응답 구조');
				setCategories([]);
				return;
			}
			
			const level1 = categoryList.filter(cat => cat.level === 1);
			
			setCategories(level1);
			
		} catch (error) {
			console.error('카테고리 로딩 실패:', error);
			setCategories([]);
		}
	};

	const fetchSituationTags = async () => {
		try {
			console.log('🔍 상황별 태그 API 호출 시작...');
			
			const response = await fetch(`${API_BASE_URL}/products/situation_tags/`);
			
			console.log('📡 응답 상태:', response.status);
			console.log('📡 Content-Type:', response.headers.get('content-type'));
			
			const contentType = response.headers.get('content-type') || '';
			if (!contentType.includes('application/json')) {
				const text = await response.text();
				console.error('❌ JSON이 아닌 응답:', text.slice(0, 500));
				return;
			}
			
			if (!response.ok) {
				throw new Error(`HTTP ${response.status}`);
			}
			
			const data = await response.json();
			console.log('✅ 태그 데이터 로드 성공:', data);
			
			if (data.tags) {
				setAllTags(data.tags);
				console.log('✅ allTags 상태 업데이트 완료');
			} else {
				console.error('❌ tags 필드가 없습니다:', data);
			}
			
		} catch (error) {
			console.error('❌ 태그 로딩 실패:', error);
		}
	};

	const fetchNotices = async () => {
		try {
			const response = await fetch(`${API_BASE_URL}/products/notices/`);
			if (response.ok) {
				const data = await response.json();
				setNotices(data.results || data || []);
			}
		} catch (error) {
			console.error('공지사항 로딩 실패:', error);
		}
	};

	const searchBySituation = async (page = 1) => {
		try {
			const hasAnyTags = Object.values(situationTags).some(tags => tags && tags.length > 0);
			if (!hasAnyTags) {
				return;
			}
			
			setLoading(true);
			const params = new URLSearchParams();
			
			Object.entries(situationTags).forEach(([key, values]) => {
				if (values && values.length > 0) {
					params.append(key, values.join(','));
				}
			});
			
			params.append('page', page);
			
			const response = await fetch(
				`${API_BASE_URL}/products/products/search_by_situation/?${params.toString()}`
			);
			const data = await response.json();
			
			setProducts(data.results || []);
			setTotalCount(data.count || 0);
			setTotalPages(data.total_pages || 0);
		} catch (error) {
			console.error('검색 실패:', error);
			setProducts([]);
			setTotalCount(0);
			setTotalPages(0);
		} finally {
			setLoading(false);
		}
	};

	const handleTagToggle = (category, code) => {
		const currentTags = situationTags[category] || [];
		const codeNum = parseInt(code);
		
		let newTags;
		if (currentTags.includes(codeNum)) {
			newTags = {
				...situationTags,
				[category]: currentTags.filter(c => c !== codeNum)
			};
		} else {
			const limits = {
				weather: 2,
				where: 3,
				mood: 2,
				situation: 2,
				season_event: 2,
				style: 3,
				color_mood: 2,
				age_target: 2  // ← 추가!
			};
			
			if (currentTags.length >= limits[category]) {
				alert(`최대 ${limits[category]}개까지 선택 가능합니다.`);
				return;
			}
			
			newTags = {
				...situationTags,
				[category]: [...currentTags, codeNum]
			};
		}
		
		setSituationTags(newTags);
		
		const params = new URLSearchParams(searchParams);
		params.set('mode', 'situation');
		
		Object.entries(newTags).forEach(([key, values]) => {
			if (values && values.length > 0) {
				params.set(key, values.join(','));
			} else {
				params.delete(key);
			}
		});
		
		setSearchParams(params, { replace: true });
		
		setShowDropdown(null);
		setCurrentPage(1);
	};

	const getTagNames = (category, codes) => {
		if (!codes || codes.length === 0) {
			const emptyTexts = {
				weather: '☀️ 날씨는?',
				where: '📍 어디 가?',
				mood: '😊 기분은?',
				situation: '🤔 상황은?',
				season_event: '🎉 시즌/이벤트',
				style: '👗 스타일은?',
				color_mood: '🎨 무슨 색?',
				age_target: '🎂 연령대?'  // 생일케이크
			};
			return emptyTexts[category] || '선택';
		}
		
		const names = codes.map(code => allTags[category]?.[code] || '').filter(Boolean);
		return names.join(', ') || '선택';
	};

	const resetSituationTags = () => {
		setSituationTags({
			weather: [],
			where: [],
			mood: [],
			situation: [],
			season_event: [],
			style: [],
			color_mood: [],
			age_target: []  // ← 추가!
		});
		setCurrentPage(1);
		
		const params = new URLSearchParams();
		params.set('mode', 'situation');
		setSearchParams(params, { replace: true });
	};

	const clearAllSelections = () => {
		setSelectedLevel1(null);
		setSelectedLevel2(null);
		setSelectedLevel3(null);
		setSituationMode(false);
		setCurrentPage(1);
		setSearchParams({});
		fetchData(1);
	};

	const handleLevel2Click = (level2Cat) => {
		const scrollY = window.scrollY;
		
		setSelectedLevel2(level2Cat);
		setSelectedLevel3(null);
		
		const params = new URLSearchParams(searchParams);
		if (selectedLevel1) {
			params.set('cat1', selectedLevel1.id);
		}
		params.set('cat2', level2Cat.id);
		params.delete('cat3');
		params.delete('page');
		setSearchParams(params);
		setCurrentPage(1);
		
		setTimeout(() => {
			window.scrollTo(0, scrollY);
		}, 0);
	};

	const handleLevel3Click = (level3Cat) => {
		setSelectedLevel3(level3Cat);
		setCurrentPage(1);
		
		const params = new URLSearchParams(searchParams);
		if (selectedLevel1) {
			params.set('cat1', selectedLevel1.id);
		}
		if (selectedLevel2) {
			params.set('cat2', selectedLevel2.id);
		}
		params.set('cat3', level3Cat.id);
		params.delete('page');
		setSearchParams(params);
	};

	const handlePageChange = (pageNumber) => {
		setCurrentPage(pageNumber);
		
		const params = new URLSearchParams(searchParams);
		if (pageNumber > 1) {
			params.set('page', pageNumber);
		} else {
			params.delete('page');
		}
		setSearchParams(params);
		
		window.scrollTo({ top: 0, behavior: 'smooth' });
	};

	const renderDropdown = (category) => {
		const isOpen = showDropdown === category;
		const selectedCodes = situationTags[category] || [];
		
		return (
			<div className="tag-dropdown" key={category}>
				<button 
					ref={el => buttonRefs.current[category] = el}
					className="tag-current"
					onClick={() => setShowDropdown(isOpen ? null : category)}
				>
					{getTagNames(category, selectedCodes)} {selectedCodes.length > 0 && `(${selectedCodes.length})`} ▼
				</button>
				
				<DropdownPortal 
					isOpen={isOpen} 
					buttonRef={{ current: buttonRefs.current[category] }}
				>
					<div className="tag-options" ref={dropdownRef}>
						{allTags[category] && Object.entries(allTags[category]).map(([code, name]) => (
							<button
								key={code}
								className={selectedCodes.includes(parseInt(code)) ? 'active' : ''}
								onClick={() => handleTagToggle(category, code)}
							>
								{selectedCodes.includes(parseInt(code)) && '✓ '}
								{name}
							</button>
						))}
					</div>
				</DropdownPortal>
			</div>
		);
	};

	const renderPagination = () => {
		if (totalPages <= 1) return null;

		return (
			<div className="pagination">
				<button
					onClick={() => handlePageChange(1)}
					disabled={currentPage === 1}
					className="page-btn"
				>
					처음
				</button>
				<button
					onClick={() => handlePageChange(currentPage - 1)}
					disabled={currentPage === 1}
					className="page-btn"
				>
					이전
				</button>

				{[...Array(totalPages)].map((_, index) => {
					const pageNumber = index + 1;
					if (
						pageNumber === 1 ||
						pageNumber === totalPages ||
						(pageNumber >= currentPage - 2 && pageNumber <= currentPage + 2)
					) {
						return (
							<button
								key={pageNumber}
								onClick={() => handlePageChange(pageNumber)}
								className={currentPage === pageNumber ? 'page-btn active' : 'page-btn'}
							>
								{pageNumber}
							</button>
						);
					} else if (
						pageNumber === currentPage - 3 ||
						pageNumber === currentPage + 3
					) {
						return <span key={pageNumber}>...</span>;
					}
					return null;
				})}

				<button
					onClick={() => handlePageChange(currentPage + 1)}
					disabled={currentPage === totalPages}
					className="page-btn"
				>
					다음
				</button>
				<button
					onClick={() => handlePageChange(totalPages)}
					disabled={currentPage === totalPages}
					className="page-btn"
				>
					마지막
				</button>
			</div>
		);
	};

	const renderNoticeBanner = () => {
		if (notices.length === 0) {
			return (
				<div className="notice-banner">
					<div className="notice-content">
						<span>📢 공지사항을 불러오는 중...</span>
					</div>
				</div>
			);
		}

		return (
			<div className="notice-banner">
				<div className="notice-content">
					{notices.map(notice => (
						<span 
							key={notice.id} 
							onClick={() => navigate(`/notice/${notice.id}`)}
							style={{ cursor: 'pointer' }}
						>
							{notice.title}
						</span>
					))}
					{notices.map(notice => (
						<span 
							key={`dup-${notice.id}`}
							onClick={() => navigate(`/notice/${notice.id}`)}
							style={{ cursor: 'pointer' }}
						>
							{notice.title}
						</span>
					))}
				</div>
			</div>
		);
	};

	const renderTopBar = () => {
		if (situationMode) {
			return (
				<div className="situation-tag-bar">
					{renderDropdown('weather')}
					{renderDropdown('where')}
					{renderDropdown('mood')}
					{renderDropdown('situation')}
					{renderDropdown('season_event')}
					{renderDropdown('style')}
					{renderDropdown('color_mood')}
					{renderDropdown('age_target')}  // ← 추가!
					<button className="btn-reset-tags" onClick={resetSituationTags}>
						🔄 초기화
					</button>
				</div>
			);
		}
		
		if (selectedLevel1) {
			return (
				<div className="category-navigation">
					<div className="category-level2">
						{selectedLevel1.children && selectedLevel1.children.map(level2 => (
							<button
								key={level2.id}
								className={`cat-btn ${selectedLevel2?.id === level2.id ? 'active' : ''}`}
								onClick={() => handleLevel2Click(level2)}
							>
								{level2.name}
							</button>
						))}
					</div>
				</div>
			);
		}
		
		return renderNoticeBanner();
	};

	const renderLevel3Bar = () => {
		if (!selectedLevel2 || !selectedLevel2.children || selectedLevel2.children.length === 0) {
			return null;
		}
		
		return (
			<div className="category-navigation">
				<div className="category-level3">
					{selectedLevel2.children.map(level3 => (
						<button
							key={level3.id}
							className={`cat-btn-small ${selectedLevel3?.id === level3.id ? 'active' : ''}`}
							onClick={() => handleLevel3Click(level3)}
						>
							{level3.name}
						</button>
					))}
				</div>
			</div>
		);
	};

	if (loading) {
		return (
			<div className="home">
				{renderTopBar()}
				{renderLevel3Bar()}
				
				<div className="skeleton-container">
					<div className="skeleton-message">
						<span className="skeleton-emoji">🤔</span>
						<span className="skeleton-text">오늘 뭐 입지?</span>
					</div>
					<div className="products-grid">
						{[...Array(16)].map((_, index) => (
							<div key={index} className="skeleton-card">
								<div className="skeleton-image"></div>
								<div className="skeleton-info">
									<div className="skeleton-title"></div>
									<div className="skeleton-title short"></div>
									<div className="skeleton-price"></div>
								</div>
							</div>
						))}
					</div>
				</div>
			</div>
		);
	}

	const filteredProducts = products;

	return (
		<div className="home">
			{renderTopBar()}
			{renderLevel3Bar()}

			{searchTerm && !situationMode && (
				<>
					{filteredProducts.length > 0 && (
						<div className="result-info">
							<span className="result-count">
								총 <strong>{totalCount}</strong>개 상품
							</span>
							<span className="search-keyword">'{searchTerm}' 검색 결과</span>
							<button className="btn-clear" onClick={clearAllSelections}>
								✕ 초기화
							</button>
						</div>
					)}

					{filteredProducts.length === 0 ? (
						<div className="no-products">
							<div className="no-products-icon">📦</div>
							<p>상품이 없습니다.</p>
							<button className="btn-reset" onClick={clearAllSelections}>
								전체 상품 보기
							</button>
						</div>
					) : (
						<>
							<div className="products-grid">
								{filteredProducts.map(product => (
									<ProductCard 
										key={product.id} 
										product={product} 
										onClick={handleProductClick}
									/>
								))}
							</div>
							{renderPagination()}
						</>
					)}
				</>
			)}

			{(selectedLevel1 || selectedLevel2 || selectedLevel3) && !searchTerm && !situationMode && (
				<>
					{filteredProducts.length === 0 ? (
						<div className="no-products">
							<div className="no-products-icon">📦</div>
							<p>상품이 없습니다.</p>
							<button className="btn-reset" onClick={clearAllSelections}>
								전체 상품 보기
							</button>
						</div>
					) : (
						<>
							<div className="products-grid">
								{filteredProducts.map(product => (
									<ProductCard 
										key={product.id} 
										product={product} 
										onClick={handleProductClick}
									/>
								))}
							</div>
							{renderPagination()}
						</>
					)}
				</>
			)}

			{situationMode && (
				<>
					<div className="products-grid">
						{products.map(product => (
							<ProductCard 
								key={product.id} 
								product={product} 
								onClick={handleProductClick}
							/>
						))}
					</div>
					{renderPagination()}
				</>
			)}

			{!selectedLevel1 && !searchTerm && !situationMode && (
				<>
					<div className="products-grid">
						{products.map(product => (
							<ProductCard 
								key={product.id} 
								product={product} 
								onClick={handleProductClick}
							/>
						))}
					</div>
					{renderPagination()}
				</>
			)}
		</div>
	);
}

export default Home;